import { useState, useEffect } from 'react'
import { PURPLE, PURPLE_MID } from '~/client/components/recs/constants'
import { PurplePin } from '~/client/components/recs/PurplePin'

interface TransitionScreenProps {
  eventName: string
  city: string
  guests: number
  venueCount?: number
  onDone: () => void
}

export function TransitionScreen({ city, venueCount, onDone }: TransitionScreenProps) {
  const [count, setCount] = useState(0)
  const [step, setStep] = useState(0) // 0 = scanning, 1 = found
  const target = venueCount || 2830

  useEffect(() => {
    const start = Date.now()
    const duration = 1200
    const raf = () => {
      const p = Math.min((Date.now() - start) / duration, 1)
      const ease = 1 - Math.pow(1 - p, 3)
      setCount(Math.round(ease * target))
      if (p < 1) {
        requestAnimationFrame(raf)
      } else {
        setStep(1)
        setTimeout(onDone, 900)
      }
    }
    requestAnimationFrame(raf)
  }, [onDone])

  const pins = [
    { x: -160, y: -80, delay: 0, rot: -15 },
    { x: 140, y: -100, delay: 0.1, rot: 10 },
    { x: -80, y: 80, delay: 0.05, rot: 5 },
    { x: 160, y: 60, delay: 0.15, rot: -8 },
    { x: 0, y: -140, delay: 0.08, rot: 0 },
  ]

  return (
    <div
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center bg-[#FEFBF6]"
      style={{ animation: 'recs-tFadeIn 0.25s ease both' }}
    >
      {/* Expanding purple circle burst */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: step === 1 ? `${300 + i * 180}px` : '0px',
              height: step === 1 ? `${300 + i * 180}px` : '0px',
              background:
                i === 0
                  ? `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`
                  : `rgba(107,33,232,${0.06 - i * 0.02})`,
              border: i > 0 ? `1px solid rgba(107,33,232,${0.15 - i * 0.04})` : 'none',
              transition: `all ${0.5 + i * 0.15}s cubic-bezier(0.16,1,0.3,1) ${i * 0.07}s`,
              opacity: step === 1 ? 1 : 0,
            }}
          />
        ))}
      </div>

      {/* Floating pins */}
      {pins.map((p, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `calc(50% + ${p.x}px)`,
            top: `calc(50% + ${p.y}px)`,
            transform: `rotate(${p.rot}deg) scale(${step === 1 ? 1 : 0})`,
            opacity: step === 1 ? 0.85 : 0,
            transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${p.delay + 0.1}s`,
          }}
        >
          <PurplePin size={step === 1 ? 24 : 0} />
        </div>
      ))}

      {/* Center content */}
      <div className="relative z-[2] px-6 text-center">
        <div
          className="mb-2.5 text-[13px] font-semibold uppercase tracking-wider"
          style={{
            color: step === 1 ? '#fff' : 'rgba(107,33,232,0.6)',
            transition: 'color 0.4s ease',
            opacity: step === 1 ? 0 : 1,
          }}
        >
          Scanning {city}
        </div>

        <div
          className="font-extrabold leading-none"
          style={{
            fontSize: 72,
            letterSpacing: '-3px',
            color: step === 1 ? '#fff' : PURPLE,
            transition: 'color 0.35s ease',
            fontFamily: "'Inter', sans-serif",
            marginBottom: step === 1 ? 0 : 8,
          }}
        >
          {count.toLocaleString()}
        </div>

        {step === 0 && <div className="mt-2 text-sm text-gray-400">spaces in {city}</div>}

        {step === 1 && (
          <div className="mt-4" style={{ animation: 'recs-tFadeUp 0.4s ease both' }}>
            <div className="mb-1 text-xl font-bold" style={{ color: '#2D1B69' }}>Ready to find your perfect venue.</div>
            <div className="text-sm text-white/70">powered by Revel Street venue intelligence</div>
          </div>
        )}

        {step === 0 && (
          <div className="mx-auto mt-5 h-[3px] w-60 overflow-hidden rounded-full bg-[#E9E8E3]">
            <div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${PURPLE}, ${PURPLE_MID})`,
                width: `${(count / target) * 100}%`,
                transition: 'width 0.05s linear',
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
